import { ChevronDown, Clock, Crown, Star } from "lucide-react";
import React from "react";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  type AppointmentOutcome,
  getOutcomeStatusInfo,
  OUTCOME_STATUS_VALUES,
} from "@/domains/appointment/appointments/shared/utils/appointment-status-style";
import { timezoneUtils } from "@/lib/timezone";
import { useWorkerActions } from "../hooks";
import { useCurrentWorker } from "../hooks/use-current-worker";
import type { WorkerAppointment } from "../types";

interface WorkerAppointmentsTableProps {
  appointments: WorkerAppointment[];
  showQuickActions?: boolean;
  onAppointmentClick?: (appointment: WorkerAppointment) => void;
}

interface GroupedAppointments {
  [date: string]: WorkerAppointment[];
}

export function WorkerAppointmentsTable({
  appointments,
  showQuickActions = true,
  onAppointmentClick,
}: WorkerAppointmentsTableProps) {
  const { currentWorker } = useCurrentWorker();
  const { startService, completeService, markNoShow } = useWorkerActions();

  // Group appointments by date
  const groupedAppointments = React.useMemo((): GroupedAppointments => {
    return appointments.reduce((acc, appointment) => {
      const startTime = timezoneUtils.fromISO(appointment.startTime);
      const dateKey = startTime.toFormat("yyyy-MM-dd");
      if (!acc[dateKey]) {
        acc[dateKey] = [];
      }
      acc[dateKey].push(appointment);
      return acc;
    }, {} as GroupedAppointments);
  }, [appointments]);

  // Get customer tier info
  const getTierInfo = (tier: string | null | undefined) => {
    if (!tier) return null;

    switch (tier.toLowerCase()) {
      case "vip":
      case "premium":
        return {
          icon: Crown,
          color: "text-yellow-600",
          bgColor: "bg-yellow-50 dark:bg-yellow-950/20",
        };
      case "gold":
        return {
          icon: Crown,
          color: "text-yellow-500",
          bgColor: "bg-yellow-50 dark:bg-yellow-950/20",
        };
      case "silver":
        return { icon: Star, color: "text-gray-500", bgColor: "bg-gray-50 dark:bg-gray-950/20" };
      case "bronze":
        return {
          icon: Star,
          color: "text-orange-600",
          bgColor: "bg-orange-50 dark:bg-orange-950/20",
        };
      default:
        return { icon: Star, color: "text-blue-600", bgColor: "bg-blue-50 dark:bg-blue-950/20" };
    }
  };

  // Calculate worker services
  const getWorkerServices = (appointment: WorkerAppointment) => {
    const services = appointment.appointmentServices || [];
    const myServices = services.filter((service) => service.workerId === currentWorker?.id);
    const totalDuration = myServices.reduce((sum, service) => sum + service.serviceDuration, 0);

    return {
      services: myServices,
      totalDuration,
      hasMyServices: myServices.length > 0,
    };
  };

  // Handle outcome changes
  const handleOutcomeChange = (
    appointment: WorkerAppointment,
    outcome: AppointmentOutcome | null
  ) => {
    const appointmentId = appointment.originalId || appointment.id;

    switch (outcome) {
      case "IN_PROGRESS":
        startService(appointmentId);
        break;
      case "COMPLETED":
        completeService(appointmentId);
        break;
      case "NO_SHOW":
        markNoShow(appointmentId);
        break;
      case "RESCHEDULED":
        break;
      case null:
        break;
    }
  };

  // Sort dates
  const sortedDates = Object.keys(groupedAppointments).sort();

  if (appointments.length === 0) {
    return null;
  }

  return (
    <div className="hidden md:block space-y-6">
      {sortedDates.map((dateKey) => {
        const dayAppointments = groupedAppointments[dateKey];
        const date = timezoneUtils.fromISO(dateKey);

        return (
          <div key={dateKey}>
            {/* Day Header */}
            <div className="bg-card rounded-lg shadow-sm border border-border overflow-hidden">
              <div className="bg-muted/50 px-4 py-3 border-b border-border">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <h3 className="font-semibold text-foreground">
                      {date.toFormat("EEEE, MMMM d, yyyy")}
                    </h3>
                    <Badge variant="secondary" className="text-xs">
                      {dayAppointments.length} appointment{dayAppointments.length !== 1 ? "s" : ""}
                    </Badge>
                  </div>
                </div>
              </div>

              {/* Table */}
              <table className="w-full table-fixed">
                <colgroup>
                  <col className="w-24" />
                  <col />
                  <col className="w-40" />
                  <col className="w-32" />
                  <col className="w-24" />
                  <col className="w-24" />
                </colgroup>
                <thead>
                  <tr className="border-b border-border text-xs text-muted-foreground uppercase tracking-wider">
                    <th className="py-3 px-4 text-left">Time</th>
                    <th className="py-3 px-4 text-left">Services</th>
                    <th className="py-3 px-4 text-left">Client</th>
                    <th className="py-3 px-4 text-left">Status</th>
                    <th className="py-3 px-4 text-left">Duration</th>
                    <th className="py-3 px-4 text-right">Price</th>
                  </tr>
                </thead>
                <tbody>
                  {dayAppointments.map((appointment) => {
                    const startTime = timezoneUtils.fromISO(appointment.startTime);
                    const endTime = timezoneUtils.fromISO(appointment.endTime);
                    const customerName =
                      `${appointment.customer?.firstName || ""} ${appointment.customer?.lastName || ""}`.trim() ||
                      "Unknown Customer";
                    const tierInfo = getTierInfo(appointment.customer?.tier);
                    const currentOutcome = appointment.outcome as AppointmentOutcome | null;
                    const outcomeStatusInfo = getOutcomeStatusInfo(currentOutcome);
                    const StatusIcon = outcomeStatusInfo.icon;
                    const workerServiceInfo = getWorkerServices(appointment);

                    return (
                      <tr
                        key={appointment.id}
                        className="border-b border-border hover:bg-muted/30 cursor-pointer transition-colors"
                        onClick={() => onAppointmentClick?.(appointment)}
                      >
                        {/* Time */}
                        <td className="py-4 px-4">
                          <div className="flex flex-col">
                            <div className="font-semibold text-foreground">
                              {startTime.toFormat("HH:mm")}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {endTime.toFormat("HH:mm")}
                            </div>
                            {appointment.isConsecutiveBlock &&
                              appointment.totalBlocks &&
                              appointment.totalBlocks > 1 && (
                                <Badge
                                  variant="outline"
                                  className="text-xs text-blue-600 border-blue-300 mt-1"
                                >
                                  {(appointment.blockIndex || 0) + 1}/{appointment.totalBlocks}
                                </Badge>
                              )}
                          </div>
                        </td>

                        {/* Services */}
                        <td className="py-4 px-4">
                          <div className="space-y-1">
                            {!workerServiceInfo.hasMyServices ? (
                              <div className="text-sm text-muted-foreground">
                                No services assigned
                              </div>
                            ) : (
                              <div className="space-y-1">
                                {workerServiceInfo.services.map((service) => (
                                  <div key={service.id} className="flex items-center gap-2">
                                    <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                                    <span className="text-sm font-medium text-foreground">
                                      {service.serviceName}
                                    </span>
                                    <Badge
                                      variant="default"
                                      className="text-xs bg-blue-100 dark:bg-blue-950/30 text-blue-700 dark:text-blue-300 px-1 py-0"
                                    >
                                      {service.serviceDuration}min
                                    </Badge>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </td>

                        {/* Client */}
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-foreground truncate">
                              {customerName}
                            </span>
                            {tierInfo && (
                              <div
                                className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${tierInfo.color} ${tierInfo.bgColor}`}
                              >
                                <tierInfo.icon className="w-3 h-3" />
                                <span className="capitalize">{appointment.customer?.tier}</span>
                              </div>
                            )}
                          </div>
                        </td>

                        {/* Status */}
                        <td className="py-4 px-4">
                          {showQuickActions ? (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <div
                                  className={`flex items-center gap-1 px-2 py-1 rounded-full cursor-pointer hover:opacity-80 touch-manipulation min-h-[32px] ${outcomeStatusInfo.backgroundColor} ${outcomeStatusInfo.borderColor}`}
                                >
                                  <StatusIcon
                                    className={`w-3 h-3 ${outcomeStatusInfo.iconColor}`}
                                  />
                                  <span
                                    className={`text-xs font-medium ${outcomeStatusInfo.textColor}`}
                                  >
                                    {outcomeStatusInfo.text}
                                  </span>
                                  <ChevronDown
                                    className={`w-3 h-3 ${outcomeStatusInfo.iconColor}`}
                                  />
                                </div>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-48">
                                {OUTCOME_STATUS_VALUES.map((outcome) => {
                                  const statusInfo = getOutcomeStatusInfo(outcome);
                                  const StatusMenuIcon = statusInfo.icon;
                                  const isCurrentStatus = outcome === currentOutcome;

                                  return (
                                    <DropdownMenuItem
                                      key={outcome || "scheduled"}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleOutcomeChange(appointment, outcome);
                                      }}
                                      disabled={isCurrentStatus}
                                      className={isCurrentStatus ? "opacity-50" : ""}
                                    >
                                      <StatusMenuIcon
                                        className={`w-4 h-4 mr-2 ${statusInfo.iconColor}`}
                                      />
                                      {statusInfo.text}
                                      {isCurrentStatus && (
                                        <span className="ml-auto text-xs text-muted-foreground">
                                          Current
                                        </span>
                                      )}
                                    </DropdownMenuItem>
                                  );
                                })}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          ) : (
                            <div
                              className={`flex items-center gap-1 px-2 py-1 rounded-full min-h-[32px] ${outcomeStatusInfo.backgroundColor} ${outcomeStatusInfo.borderColor}`}
                            >
                              <StatusIcon className={`w-3 h-3 ${outcomeStatusInfo.iconColor}`} />
                              <span
                                className={`text-xs font-medium ${outcomeStatusInfo.textColor}`}
                              >
                                {outcomeStatusInfo.text}
                              </span>
                            </div>
                          )}
                        </td>

                        {/* Duration */}
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-1 text-sm text-muted-foreground">
                            <Clock className="w-3 h-3" />
                            {workerServiceInfo.totalDuration}min
                          </div>
                        </td>

                        {/* Price */}
                        <td className="py-4 px-4 text-right">
                          <div className="font-semibold text-foreground">
                            ${(appointment.totalPrice / 100).toFixed(2)}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
}
